
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useCountries } from '@/hooks/useCountries';
import { useDepartments } from '@/hooks/useDepartments';
import { useState } from 'react';

interface SupplierFormFieldsProps {
  formData: {
    name_ar: string;
    name_en: string;
    employee_name_ar: string;
    employee_name_en: string;
    mobile: string;
    country: string;
    departments: string;
    email: string;
    access_permission: boolean;
  };
  onInputChange: (field: string, value: any) => void;
}

export function SupplierFormFields({ formData, onInputChange }: SupplierFormFieldsProps) {
  const { data: countries = [], isLoading: countriesLoading } = useCountries();
  const { data: departments = [], isLoading: departmentsLoading } = useDepartments();
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>(
    formData.departments ? formData.departments.split(' - ') : []
  );

  // Filter out empty countries to prevent SelectItem value errors
  const validCountries = countries.filter(country => country.name_ar && country.name_ar.trim() !== '');

  const handleDepartmentChange = (departmentName: string, checked: boolean) => {
    let newSelectedDepartments;
    if (checked) {
      newSelectedDepartments = [...selectedDepartments, departmentName];
    } else {
      newSelectedDepartments = selectedDepartments.filter(d => d !== departmentName);
    }
    setSelectedDepartments(newSelectedDepartments);
    onInputChange('departments', newSelectedDepartments.join(' - '));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="name_ar" required>الاسم بالعربية</Label>
        <Input
          id="name_ar"
          value={formData.name_ar}
          onChange={(e) => onInputChange('name_ar', e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="name_en">الاسم بالإنجليزية</Label>
        <Input
          id="name_en"
          value={formData.name_en}
          onChange={(e) => onInputChange('name_en', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="employee_name_ar">اسم الموظف بالعربية</Label>
        <Input
          id="employee_name_ar"
          value={formData.employee_name_ar}
          onChange={(e) => onInputChange('employee_name_ar', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="employee_name_en">اسم الموظف بالإنجليزية</Label>
        <Input
          id="employee_name_en"
          value={formData.employee_name_en}
          onChange={(e) => onInputChange('employee_name_en', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="mobile">الموبايل</Label>
        <Input
          id="mobile"
          type="tel"
          value={formData.mobile}
          onChange={(e) => onInputChange('mobile', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="country">الدولة</Label>
        <Select value={formData.country} onValueChange={(value) => onInputChange('country', value)}>
          <SelectTrigger>
            <SelectValue placeholder="اختر الدولة" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {countriesLoading ? (
              <SelectItem value="loading" disabled>جاري التحميل...</SelectItem>
            ) : (
              validCountries.map((country) => (
                <SelectItem key={country.id} value={country.name_ar}>
                  {country.name_ar}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2 md:col-span-2">
        <Label htmlFor="email">البريد الإلكتروني</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => onInputChange('email', e.target.value)}
        />
      </div>

      <div className="space-y-2 md:col-span-2">
        <Label>صلاحيات الدخول للأقسام</Label>
        <div className="text-sm text-gray-600 mb-2">
          اختر الأقسام التي يمكن للمورد الوصول إليها من القائمة الجانبية
        </div>
        {departmentsLoading ? (
          <div className="text-sm text-gray-500">جاري التحميل...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-gray-50">
            {departments.map((department) => (
              <div key={department.id} className="flex items-center space-x-2 space-x-reverse p-2 hover:bg-white rounded transition-colors">
                <Checkbox
                  id={`dept-${department.id}`}
                  checked={selectedDepartments.includes(department.name)}
                  onCheckedChange={(checked) => handleDepartmentChange(department.name, checked as boolean)}
                />
                <Label htmlFor={`dept-${department.id}`} className="text-sm cursor-pointer flex-1">
                  {department.name}
                </Label>
              </div>
            ))}
          </div>
        )}
        {selectedDepartments.length > 0 && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <span className="text-sm font-medium text-blue-800">الأقسام المختارة: </span>
            <div className="mt-1 flex flex-wrap gap-1">
              {selectedDepartments.map((dept, index) => (
                <span key={dept} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                  {dept}
                  {index < selectedDepartments.length - 1 && ''}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center space-x-2 space-x-reverse md:col-span-2">
        <Switch
          id="access_permission"
          checked={formData.access_permission}
          onCheckedChange={(checked) => onInputChange('access_permission', checked)}
        />
        <Label htmlFor="access_permission">تفعيل صلاحية الدخول للنظام</Label>
      </div>
    </div>
  );
}
